import IconTooltip from '@/assets/img/icons/icon_tooltip@2x.png';
import React, { FC, ReactElement, useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { breakpoint, breakpointReverse } from '@/helpers/BreakpointHelper';

const CloseButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 20px;
  height: 20px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  color: #2d2926;
  font-size: 18px;
  line-height: 1;
  display: none;

  ${breakpoint(640)} {
    display: block;
  }

  &:hover {
    color: #1cabe2;
  }
`;

interface TooltipTextProps {
  $isOpen?: boolean;
}

const TooltipText = styled.div<TooltipTextProps>`
  font-size: 14px;
  line-height: 1.5;
  z-index: ${({ $isOpen }) => ($isOpen ? '99999' : '-99999')};
  visibility: ${({ $isOpen }) => ($isOpen ? 'visible' : 'hidden')};
  opacity: ${({ $isOpen }) => ($isOpen ? '1' : '0')};
  border: 1px solid #1cabe2;
  background: #fff;
  padding: 12px 16px;
  border-radius: 8px;
  position: absolute;
  right: 0;
  white-space: nowrap;
  transition: opacity 0.3s, visibility 0.3s;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);

  /* 화살표 테두리 (말풍선 꼭지) - 상단 오른쪽에 치우침 */
  &::before {
    content: '';
    position: absolute;
    border-left: 9px solid transparent;
    border-right: 9px solid transparent;
  }

  /* 화살표 내부 (말풍선 꼭지) */
  &::after {
    content: '';
    position: absolute;
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
  }

  /* 모바일: 화면 밖으로 나가지 않도록 */
  ${breakpoint(640)} {
    right: auto;
    left: 50%;
    transform: translateX(-50%);
    max-width: calc(100vw - 32px);
    white-space: normal;
  }
`;

interface TooltipBoxProps {
  type?: string;
  size?: number;
  position?: string;
  $isMobile?: boolean;
}

const TooltipBox = styled.div<TooltipBoxProps>`
  display: inline-block;
  position: relative;

  /* PC: hover 시 툴팁 노출 */
  ${breakpointReverse(641)} {
    &:hover {
      ${TooltipText} {
        z-index: 99999;
        opacity: 1;
        visibility: visible;
      }
    }
  }

  ${({ type }) => {
    if (type === `icon`) {
      return `
      padding-left: 28px;
      &::before {
        content: '';
        display: block;
        width: 20px;
        height: 20px;
        background: url(${IconTooltip}) no-repeat center center;
        background-size: 100%;
        position: absolute;
        left: 0;
        top: 4px;
      }
      `;
    }
    return ``;
  }}

  ${({ size }) => {
    if (size) {
      return `
      ${TooltipText} {
        width: ${size}px;
        white-space: normal;
      }
      `;
    }
    return ``;
  }}

  ${({ position }) => {
    if (position === `top`) {
      return `
      ${TooltipText} {
        bottom: calc(100% + 12px);

        /* 화살표 테두리 - 하단 오른쪽 */
        &::before {
          top: 100%;
          right: 20px;
          border-top: 9px solid #1cabe2;
        }

        /* 화살표 내부 - 하단 오른쪽 */
        &::after {
          top: calc(100% - 1px);
          right: 21px;
          border-top: 8px solid #fff;
        }
      }
      `;
    }
    if (position === `bottom`) {
      return `
      ${TooltipText} {
        top: calc(100% + 12px);

        /* 화살표 테두리 - 상단 오른쪽 */
        &::before {
          bottom: 100%;
          right: 20px;
          border-bottom: 9px solid #1cabe2;
        }

        /* 화살표 내부 - 상단 오른쪽 */
        &::after {
          bottom: calc(100% - 1px);
          right: 21px;
          border-bottom: 8px solid #fff;
        }

        /* 모바일: 화살표 중앙 정렬 */
        ${breakpoint(640)} {
          &::before {
            right: auto;
            left: 50%;
            transform: translateX(-50%);
          }

          &::after {
            right: auto;
            left: 50%;
            transform: translateX(-50%);
          }
        }
      }
      `;
    }
    return ``;
  }}
`;

interface TooltipProps extends TooltipBoxProps {
  tip?: ReactElement;
  className?: string;
}

const Tooltip: FC<TooltipProps> = ({ tip, children, className, ...props }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);

  // 모바일 감지
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 640);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // 모바일에서 바깥 영역 클릭 시 닫기
  useEffect(() => {
    if (!isMobile || !isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        tooltipRef.current &&
        !tooltipRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMobile, isOpen]);

  const handleClick = (e: React.MouseEvent) => {
    if (isMobile) {
      // 툴팁 텍스트 영역을 클릭한 경우 아무 동작도 하지 않음
      const target = e.target as HTMLElement;
      const tooltipTextElement = tooltipRef.current?.querySelector('[data-tooltip-text]');
      if (tooltipTextElement?.contains(target)) {
        return;
      }

      e.stopPropagation();
      setIsOpen(!isOpen);
    }
  };

  const handleClose = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(false);
  };

  return (
    <TooltipBox
      ref={tooltipRef}
      className={className}
      onClick={handleClick}
      $isMobile={isMobile}
      {...props}
    >
      {children}
      <TooltipText $isOpen={isOpen} data-tooltip-text>
        {tip}
        {isMobile && (
          <CloseButton onClick={handleClose} type="button" aria-label="닫기">
            ×
          </CloseButton>
        )}
      </TooltipText>
    </TooltipBox>
  );
};

export default Tooltip;
